import { useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import useHouseDocuments from '@/hooks/house/documents/useHouseDocuments';
import { useHousePermissions } from '@/hooks/useHasPermission';
import { visibleDocumentIdsFrom } from '@/utils/documentVisibility';

/**
 * Which of the property's records this reader may see, and how to link to one.
 *
 * Not the raw response: the list endpoint applies no privacy predicate, so taking every id it
 * returns would put private documents in the visible set and the filter would contain nothing.
 * Empty while the query is loading or failed, which hides source metadata rather than briefly naming
 * a record the reader has no right to.
 *
 * The plan names documents by numeric `doc_id`; the records surface is addressed by `public_id`. The
 * plan deliberately does not send the public id, so the join is made here against the house
 * documents list — the same query the records screen itself uses, so once that surface has loaded
 * this costs nothing.
 *
 * Both the list and the open card ask this, which is why it is a hook rather than something the
 * screen derives once and hands down: the query is shared, so asking twice costs nothing, and
 * neither caller has to be given an answer it could look up.
 */
export const useVisibleDocuments = (houseId: number) => {
	const { documents, isLoading } = useHouseDocuments(houseId);
	const { user, isSuperAdmin } = useAuth();
	const { canSetPrivateDocuments, canAdmin } = useHousePermissions();

	const visibleDocumentIds = useMemo(
		() =>
			visibleDocumentIdsFrom(documents, {
				canSeeEveryPrivateRecord: canSetPrivateDocuments || canAdmin || isSuperAdmin,
				userId: user?.id,
			}),
		[documents, canSetPrivateDocuments, canAdmin, isSuperAdmin, user?.id],
	);

	const publicIdById = useMemo(
		() =>
			new Map(
				documents
					.filter((document) => document.public_id)
					.map((document) => [document.id, document.public_id as string]),
			),
		[documents],
	);

	const resolveDocumentPublicId = useCallback((documentId: number) => publicIdById.get(documentId), [publicIdById]);

	return { visibleDocumentIds, resolveDocumentPublicId, isLoading };
};
